<template>
    <div>
        <pc-alert
            v-if="isValidationRule"
            shown
            v-bind:message="validationRuleHelpText" />
        <div v-if="columnVisibility.showFrequency">
            <div class="h5">
                {{ frequencySectionHeader }}
            </div>
            <pc-select
                v-model="internalRule.Frequency"
                v-bind:label="frequencyLabel"
                v-bind:options="frequencyOptions"
                v-bind:validator="requiredValidator"
                data-test-role="ruleFrequencySelector"
                italic-label />
            <pc-transition transition="slide">
                <pc-alert
                    v-bind:shown="showFrequencyOnlyFirstTimeHelpText"
                    v-bind:message="frequencyOnlyFirstTimeHelpText"
                    class="mt-2" />
            </pc-transition>
            <hr />
        </div>
        <div v-if="columnVisibility.showCondition">
            <div class="h5">
                {{ conditionSectionHeader }}
            </div>
            <pc-filtered-select
                v-model="internalRule.FormulaId"
                v-bind:label="conditionLabel"
                v-bind:items="booleanFormulaOptions"
                v-bind:required="isValidationRule"
                v-bind:validator="isValidationRule ? requiredValidator : null"
                v-bind:placeholder-text="chooseOption"
                data-test-role="ruleConditionSelector"
                italic-label
                text-prop="text"
                value-prop="value" />
        </div>
        <div v-if="columnVisibility.showErrorMessage">
            <pc-input
                v-model="internalRule.ErrorMessage"
                v-bind:label="errorMessageLabel"
                required
                v-bind:validator="requiredValidator"
                data-test-role="ruleErrorMessageInput"
                class="mt-2" />
        </div>
        <div v-if="columnVisibility.showAction">
            <hr />
            <div class="h5">
                {{ actionSectionHeader }}
            </div>
            <pc-select
                v-model.number="internalRule.Action.Type"
                v-bind:label="actionLabel"
                v-bind:options="actionOptions"
                required
                v-bind:validator="requiredValidator"
                italic-label
                data-test-role="ruleTypeSelector">
                <option value="">
                    {{ chooseOption }}
                </option>
            </pc-select>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.SendCommunication">
                <pc-filtered-select
                    v-model="internalRule.Action.CommunicationTemplateId"
                    v-bind:items="programData.CommunicationTemplates"
                    class="mt-2"
                    v-bind:label="communicationTemplateHeader"
                    v-bind:placeholder-text="chooseOption"
                    required
                    v-bind:validator="requiredValidator"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.ChangeStatus">
                <pc-filtered-select
                    v-model="internalRule.Action.StatusId"
                    v-bind:items="programData.Statuses"
                    v-bind:label="statusHeader"
                    class="mt-2"
                    required
                    v-bind:validator="requiredValidator"
                    data-test-role="ruleChangeStatusSelector"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.SetDataField">
                <!-- datafields  -->
                <pc-select
                    v-model="setDataSubType"
                    v-bind:options="setDataSubTypeOptions"
                    v-bind:label="setDataActionSubTypeLabel"
                    class="mt-2"
                    v-bind:validator="requiredValidator"
                    data-test-role="ruleSubTypeSelector"
                    required>
                    <option value="">
                        {{ chooseOption }}
                    </option>
                </pc-select>
                <pc-input
                    v-if="setDataShowValue"
                    v-model="internalRule.Action.DataFieldValue"
                    v-bind:label="setDataValueLabel"
                    class="mt-2"
                    v-bind:validator="dataValueValidator"
                    data-test-role="ruleDataValueInput"
                    required />
                <pc-select
                    v-if="setDataShowBooleanOptions"
                    v-model="internalRule.Action.DataFieldValue"
                    v-bind:label="setDataValueLabel"
                    class="mt-2"
                    v-bind:validator="requiredValidator"
                    required>
                    <option value="">
                        {{ chooseOption }}
                    </option>
                    <option value="true">
                        {{ setDataTrueValue }}
                    </option>
                    <option value="false">
                        {{ setDataFalseValue }}
                    </option>
                </pc-select>
                <pc-select
                    v-if="setDataSubTypeIsCopyOrFormula"
                    v-model="setDataSourceType"
                    v-bind:options="setDataAvailableSourceTypes"
                    v-bind:label="setDataSourceTypeLabel"
                    class="mt-2"
                    v-bind:validator="requiredValidator"
                    required>
                    <option value="">
                        {{ chooseOption }}
                    </option>
                </pc-select>
                <pc-select
                    v-if="setDataSubType === SET_DATA_SUBACTION_TYPE.copy"
                    v-model="internalRule.Action.SourceDataId"
                    v-bind:disabled="!setDataSourceType"
                    v-bind:options="setDataAvailableSources"
                    v-bind:label="setDataCopySourceLabel"
                    required
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                    <option value="">
                        {{ chooseOption }}
                    </option>
                </pc-select>
                <pc-select
                    v-if="setDataSubType === SET_DATA_SUBACTION_TYPE.formula"
                    v-model="internalRule.Action.SourceFormulaId"
                    v-bind:disabled="!setDataSourceType"
                    v-bind:options="setDataAvailableSources"
                    v-bind:label="setDataCopySourceLabel"
                    required
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                    <option value="">
                        {{ chooseOption }}
                    </option>
                </pc-select>
                <pc-filtered-select
                    v-if="setDataSubType"
                    v-model="internalRule.Action.DataId"
                    v-bind:disabled="setDataSubTypeIsCopyOrFormula && !setDataSourceType"
                    v-bind:items="setDataAvailableTargets"
                    v-bind:label="setDataTargetLabel"
                    v-bind:placeholder-text="chooseOption"
                    data-test-role="ruleDataTargetSelector"
                    required
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-alert
                    v-if="programData.TextValidationRulesEnabled && dataFieldHasTVR"
                    class="mt-2"
                    shown
                    v-bind:message="setDataFieldTVRInfoText" />
                <pc-alert
                    v-bind:shown="isSetDataPvStorageMismatch"
                    v-bind:severity="warningSeverity"
                    v-bind:message="setDataPvStorageMismatchMessage"
                    class="mt-2" />
                <pc-checkbox
                    v-if="setDataShowAppendConsecutive"
                    v-model="internalRule.Action.AppendConsecutiveNumber"
                    v-bind:label="setDataAppendConsecutiveNumberLabel"
                    data-test-role="ruleAppendConsecutiveCheckbox"
                    class="mt-2" />
                <pc-alert
                    v-bind:shown="internalRule.Action.AppendConsecutiveNumber"
                    v-bind:severity="infoSeverity"
                    v-bind:message="setDataAppendConsecutiveNumberInfo"
                    class="mt-2" />
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.CallWebAdapter">
                <pc-filtered-select
                    v-model="internalRule.Action.AdapterConfigurationId"
                    v-bind:items="programData.AdapterConfigurations"
                    v-bind:label="connectionConfigurationHeader"
                    v-bind:placeholder-text="chooseOption"
                    required
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="ConfigurationName"
                    value-prop="ConfigurationId">
                </pc-filtered-select>
                <div v-if="programData.AdapterConfigurations.length <= 0">
                    <span class="text-danger fw-bold">
                        {{ connectionConfigurationMissingError }}
                    </span>
                </div>
            </div>
            <div v-if="actionTypeIsDeadline">
                <pc-filtered-select
                    v-model="internalRule.Action.DeadlineId"
                    v-bind:items="programData.Deadlines"
                    v-bind:disabled="disableDeadlineSelect"
                    v-bind:label="deadlineHeader"
                    class="mt-2"
                    v-bind:placeholder-text="chooseOption"
                    required
                    v-bind:validation-enabled="!internalRule.Action.SatisfyAllDeadlines"
                    v-bind:validator="requiredValidator"
                    text-prop="Name"
                    data-test-role="ruleDeadlineSelector"
                    value-prop="PublicId">
                </pc-filtered-select>
                <div
                    v-if="internalRule.Action.Type === ACTION_TYPE.SetDeadline"
                    class="fst-italic">
                    <span class="fw-bold">{{ deadlineNoteHeader }}&nbsp;</span>
                    <span>{{ deadlineNoteText }}</span>
                </div>
                <div v-if="internalRule.Action.Type === ACTION_TYPE.SatisfyDeadline">
                    <pc-checkbox
                        id="satisfyAllCheckbox"
                        v-model="internalRule.Action.SatisfyAllDeadlines"
                        v-bind:label="satisfyAllDeadlinesCheckboxHeader" />
                </div>
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.GenerateAttachment">
                <pc-filtered-select
                    v-model="internalRule.Action.DocumentTemplateId"
                    v-bind:items="documentTemplates"
                    v-bind:label="sourceDocumentTemplateHeader"
                    v-bind:placeholder-text="chooseOption"
                    required
                    data-test-role="ruleDocumentSourceSelector"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-filtered-select
                    v-model="internalRule.Action.AttachmentId"
                    v-bind:items="programData.Attachments"
                    v-bind:label="attachmentToGenerateHeader"
                    v-bind:placeholder-text="chooseOption"
                    required
                    data-test-role="ruleAttachmentTargetSelector"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-select
                    v-model="generateFileType"
                    v-bind:options="generateFileTypeOptions"
                    label="File type"
                    class="mt-2"
                    data-test-role="ruleDocumentTypeSelector"
                    v-bind:validator="requiredValidator" />
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.SignalToChannel">
                <pc-filtered-select
                    v-model="internalRule.Action.ChannelId"
                    v-bind:items="channelsWithRules"
                    v-bind:label="channelHeader"
                    required
                    v-bind:placeholder-text="chooseOption"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-filtered-select
                    v-model="internalRule.Action.ChannelRuleId"
                    v-bind:items="selectedChannelSignals"
                    v-bind:label="signalHeader"
                    v-bind:disabled="!internalRule.Action.ChannelId"
                    required
                    v-bind:placeholder-text="chooseOption"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="StaticValue"
                    value-prop="PublicId"
                    hidden-prop="IsArchived">
                </pc-filtered-select>
            </div>
            <div v-if="actionTypeIsAccessGroup">
                <pc-filtered-select
                    v-model="internalRule.Action.AccessGroupId"
                    v-bind:items="programData.AccessGroups"
                    v-bind:disabled="internalRule.Action.RemoveAllAccessGroups"
                    v-bind:validation-enabled="!internalRule.Action.RemoveAllAccessGroups"
                    v-bind:label="accessGroupHeader"
                    required
                    v-bind:placeholder-text="chooseOption"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-checkbox
                    v-if="internalRule.Action.Type === ACTION_TYPE.RemoveAccessGroup"
                    id="removeAllAccessGroupsCheckbox"
                    v-model="internalRule.Action.RemoveAllAccessGroups"
                    v-bind:label="removeAllAccessGroupsCheckboxHeader" />
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.RunReport">
                <pc-filtered-select
                    v-model="internalRule.Action.ReportId"
                    v-bind:items="programData.Reports"
                    v-bind:label="selectReportHeader"
                    required
                    v-bind:placeholder-text="chooseOption"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
                <pc-alert
                    shown
                    class="mt-2"
                    v-bind:message="selectReportMessage" />
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.RequestESignature">
                <pc-filtered-select
                    v-model="internalRule.Action.EnvelopeDefinitionId"
                    v-bind:items="programData.ESignatureEnvelopes"
                    v-bind:label="selectESignatureEnvelopeHeader"
                    required
                    v-bind:placeholder-text="chooseOption"
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
            </div>
            <div v-if="internalRule.Action.Type === ACTION_TYPE.ClearDataField">
                <pc-select
                    v-model="setDataSubType"
                    v-bind:options="clearDataSubTypeOptions"
                    v-bind:label="setDataActionSubTypeLabel"
                    class="mt-2"
                    v-bind:validator="requiredValidator"
                    data-test-role="ruleSubTypeSelector"
                    required>
                    <option value="">
                        {{ chooseOption }}
                    </option>
                </pc-select>
                <pc-filtered-select
                    v-if="setDataSubType"
                    v-model="internalRule.Action.DataId"
                    v-bind:items="setDataAvailableTargets"
                    v-bind:label="clearDataNormalTargetLabel"
                    v-bind:placeholder-text="chooseOption"
                    data-test-role="ruleDataTargetSelector"
                    required
                    v-bind:validator="requiredValidator"
                    class="mt-2"
                    text-prop="Name"
                    value-prop="PublicId">
                </pc-filtered-select>
            </div>
        </div>
        <div v-if="columnVisibility.showContinueProcessing">
            <pc-select
                v-model="stopFurtherProcessing"
                v-bind:label="continueProcessingHeader"
                class="mt-2"
                data-test-role="ruleContinueProcessingSelector"
                v-bind:disabled="internalRule.Action.Type === ACTION_TYPE.ChangeStatus"
                v-bind:options="continueProcessingOptions" />
            <pc-alert
                v-bind:shown="stopFurtherProcessing === 'true'"
                v-bind:message="stopFurtherProcessingInfoText"
                class="mt-2" />
        </div>
    </div>
</template>

<script>
    import { ValidationHub } from "Validation/validationHub";
    import { RULE_TYPE, RULE_FREQUENCY, ACTION_TYPE, TRIGGER_TYPE, TRIGGER_ACTION_TYPE } from "Types/automationTypes";
    import { DIALOG_HOOK, DIALOG_RESULT } from "Components/pc-dialog.vue";
    import {
        getFrequencyText,
        getConditionText,
        getContinueProcessingText
    } from "Automations/rules/ruleTextI18nHelper";
    import { PROGRAM_DATA_TYPES, PROGRAM_DATA_NAMESPACE } from "Automations/programData/programDataStore";
    import { EDIT_AUTOMATION_TYPES, EDIT_AUTOMATION_NAMESPACE } from "./editAutomationStore";
    import {
        DATA_FIELD_TYPE,
        DATA_FIELD_GROUP_TYPE,
        getDataFieldTypeDescription,
        getDataFieldGroupTypeDescription
    } from "Types/dataFieldTypes";
    import { getActionName } from "./actionName";
    import { KEYS, NAMESPACE } from "./i18n/createOrEditRule.i18n";
    import { mapI18n } from "Util/i18n";
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
    import { getRuleColumnVisibility } from "Automations/rules/ruleColumnVisHelper";
    import { DOCUMENT_TYPE } from "DocumentTemplates/documentTemplateConstants.js";
    import { ALERT_SEVERITY } from "Util/alerts";

    const SET_DATA_SUBACTION_TYPE = {
        text: "text",
        date: "date",
        timestamp: "timestamp",
        integer: "integer",
        decimal: "decimal",
        boolean: "boolean",
        copy: "copy",
        formula: "formula"
    };

    const SET_DATA_SUBACTION_TYPE_TO_DATA_FIELD_TYPE = {
        [SET_DATA_SUBACTION_TYPE.text]: DATA_FIELD_TYPE.String,
        [SET_DATA_SUBACTION_TYPE.date]: DATA_FIELD_TYPE.DateTime,
        [SET_DATA_SUBACTION_TYPE.timestamp]: DATA_FIELD_TYPE.Timestamp,
        [SET_DATA_SUBACTION_TYPE.integer]: DATA_FIELD_TYPE.Integer,
        [SET_DATA_SUBACTION_TYPE.decimal]: DATA_FIELD_TYPE.Decimal,
        [SET_DATA_SUBACTION_TYPE.boolean]: DATA_FIELD_TYPE.Boolean
    };

    const DATA_FIELD_TYPE_TO_SET_DATA_SUBACTION_TYPE = {
        [DATA_FIELD_TYPE.String]: SET_DATA_SUBACTION_TYPE.text,
        [DATA_FIELD_TYPE.Choice]: SET_DATA_SUBACTION_TYPE.text,
        [DATA_FIELD_TYPE.DateTime]: SET_DATA_SUBACTION_TYPE.date,
        [DATA_FIELD_TYPE.Timestamp]: SET_DATA_SUBACTION_TYPE.timestamp,
        [DATA_FIELD_TYPE.Integer]: SET_DATA_SUBACTION_TYPE.integer,
        [DATA_FIELD_TYPE.Decimal]: SET_DATA_SUBACTION_TYPE.decimal,
        [DATA_FIELD_TYPE.Boolean]: SET_DATA_SUBACTION_TYPE.boolean
    };

    const GENERATE_FILE_TYPE = {
        docx: "docx",
        pdf: "pdf",
        default: "default"
    };

    const PV_STORAGE_CONFIG = {
        never: 0,
        always: 1,
        formula: 2
    };

    const FIELD_MARKER = "f";
    const GROUP_MARKER = "g";
    const PV_SYSTEM_GROUP_ID = `${GROUP_MARKER}${DATA_FIELD_GROUP_TYPE.PVSystem}`;

    export default {
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            rule: { type: Object, required: true },
            ruleType: { type: String, required: true }
        },
        data() {
            let setDataSubType = null;
            let setDataSourceType = null;
            const internalRule = JSON.parse(JSON.stringify(this.rule));

            // Set the action type for validation actions since the selector doesn't appear
            if (this.ruleType === RULE_TYPE.validation) {
                internalRule.Action.Type = ACTION_TYPE.Validate;
            }

            // Initialize setDataSubType and setDataSourceType if needed
            if (
                (this.rule.Action.Type === ACTION_TYPE.SetDataField ||
                    this.rule.Action.Type === ACTION_TYPE.ClearDataField) &&
                this.rule.Action.DataId
            ) {
                const programData = this.$store.state[PROGRAM_DATA_NAMESPACE];

                const dataField = programData.DataFields.find((df) => df.PublicId === this.rule.Action.DataId);
                const dataFieldType = dataField.TypeId.substring(FIELD_MARKER.length);
                setDataSubType = DATA_FIELD_TYPE_TO_SET_DATA_SUBACTION_TYPE[dataFieldType];

                if (this.rule.Action.SourceDataId) {
                    setDataSourceType = dataField.TypeId;
                    setDataSubType = SET_DATA_SUBACTION_TYPE.copy;
                }
                if (this.rule.Action.SourceFormulaId) {
                    setDataSubType = SET_DATA_SUBACTION_TYPE.formula;
                    const formula = programData.Formulas.find((f) => f.PublicId === this.rule.Action.SourceFormulaId);
                    setDataSourceType = formula.ResultTypeId;
                }
            }

            return {
                validationHub: new ValidationHub(),
                internalRule,

                setDataSubType,
                setDataSourceType,

                infoSeverity: ALERT_SEVERITY.info,
                warningSeverity: ALERT_SEVERITY.warning
            };
        },
        computed: {
            programData() {
                return this.$store.state[PROGRAM_DATA_NAMESPACE];
            },
            trigger() {
                return this.$store.state[EDIT_AUTOMATION_NAMESPACE].Trigger;
            },
            formulaTypes() {
                return this.$store.state[EDIT_AUTOMATION_NAMESPACE].FormulaTypes;
            },
            fieldTypes() {
                return this.$store.state[EDIT_AUTOMATION_NAMESPACE].FieldTypes;
            },
            allowRequestESignatures() {
                return this.programData.ESignatureEnvelopesEnabled && this.trigger.Type !== TRIGGER_TYPE.Scheduled;
            },
            dataValueValidator() {
                const validatorTypes = [VALIDATOR_TYPES.required];
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.integer) {
                    validatorTypes.push(VALIDATOR_TYPES.integer);
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.decimal) {
                    validatorTypes.push(VALIDATOR_TYPES.decimal);
                }
                return getValidator(this.$app, validatorTypes);
            },
            columnVisibility() {
                return getRuleColumnVisibility(this.ruleType);
            },
            actionTypeIsDeadline() {
                return (
                    this.internalRule.Action.Type === ACTION_TYPE.SetDeadline ||
                    this.internalRule.Action.Type === ACTION_TYPE.PauseDeadline ||
                    this.internalRule.Action.Type === ACTION_TYPE.ResumeDeadline ||
                    this.internalRule.Action.Type === ACTION_TYPE.SatisfyDeadline
                );
            },
            actionTypeIsAccessGroup() {
                return (
                    this.internalRule.Action.Type === ACTION_TYPE.AddAccessGroup ||
                    this.internalRule.Action.Type === ACTION_TYPE.RemoveAccessGroup
                );
            },
            disableDeadlineSelect() {
                return (
                    this.internalRule.Action.Type === ACTION_TYPE.SatisfyDeadline &&
                    this.internalRule.Action.SatisfyAllDeadlines
                );
            },
            generateFileType: {
                get() {
                    if (this.internalRule.Action.GenerateAsDocx === true) {
                        return GENERATE_FILE_TYPE.docx;
                    } else if (this.internalRule.Action.GenerateAsDocx === false) {
                        return GENERATE_FILE_TYPE.pdf;
                    }
                    return GENERATE_FILE_TYPE.default;
                },
                set(value) {
                    if (value === GENERATE_FILE_TYPE.docx) {
                        this.internalRule.Action.GenerateAsDocx = true;
                    } else if (value === GENERATE_FILE_TYPE.pdf) {
                        this.internalRule.Action.GenerateAsDocx = false;
                    } else {
                        this.internalRule.Action.GenerateAsDocx = null;
                    }
                }
            },
            stopFurtherProcessing: {
                get() {
                    return Boolean(this.internalRule.StopFurtherProcessing).toString();
                },
                set(value) {
                    this.internalRule.StopFurtherProcessing = value === "true";
                }
            },
            continueProcessingOptions() {
                return [
                    { value: "false", text: getContinueProcessingText(this.$app, false) },
                    { value: "true", text: getContinueProcessingText(this.$app, true) }
                ];
            },
            frequencyOptions() {
                return [
                    {
                        value: RULE_FREQUENCY.EveryTime,
                        text: getFrequencyText(this.$app, RULE_FREQUENCY.EveryTime)
                    },
                    {
                        value: RULE_FREQUENCY.OnlyFirstTime,
                        text: getFrequencyText(this.$app, RULE_FREQUENCY.OnlyFirstTime),
                        hidden: this.triggerIsInquiry || this.triggerIsChannel || this.isESignatureTrigger
                    }
                ];
            },
            showFrequencyOnlyFirstTimeHelpText() {
                return this.internalRule.Frequency == RULE_FREQUENCY.OnlyFirstTime;
            },
            booleanFormulaOptions() {
                const booleanFormulas = this.$store.getters[PROGRAM_DATA_TYPES.getters.getFormulasByResultType](
                    DATA_FIELD_TYPE.Boolean
                );
                const options = booleanFormulas.map((f) => ({ value: f.PublicId, text: f.Name }));
                if (!this.isValidationRule) {
                    options.unshift({ value: "", text: getConditionText(this.$app, null) });
                }
                return options;
            },
            dataFieldHasTVR() {
                const dataField =
                    this.$store.getters[PROGRAM_DATA_TYPES.getters.dataFieldsById][this.internalRule.Action.DataId];
                return dataField !== undefined && dataField?.TextValidationRuleId !== null;
            },
            isValidationRule() {
                return this.ruleType === RULE_TYPE.validation;
            },
            triggerIsInquiry() {
                return this.trigger.Type === TRIGGER_TYPE.Inquiry;
            },
            triggerIsChannel() {
                return this.trigger.Type === TRIGGER_TYPE.Channel;
            },
            isESignatureTrigger() {
                return (
                    this.trigger.ActionType === TRIGGER_ACTION_TYPE.FormESignaturesAllComplete ||
                    this.trigger.ActionType === TRIGGER_ACTION_TYPE.FormESignaturesOneFailed ||
                    this.trigger.ActionType === TRIGGER_ACTION_TYPE.FormESigsAnyESigComplete ||
                    this.trigger.ActionType === TRIGGER_ACTION_TYPE.ESignatureEnvelopeComplete ||
                    this.trigger.ActionType === TRIGGER_ACTION_TYPE.ESignatureEnvelopeAnySigned
                );
            },
            availableActionTypes() {
                if (this.ruleType === RULE_TYPE.program) {
                    return [ACTION_TYPE.RunReport];
                }
                const actionTypes = [ACTION_TYPE.SendCommunication];
                if (this.programData.HasCustomizations) {
                    actionTypes.push(ACTION_TYPE.CallWebAdapter);
                }
                if (this.trigger.Type === TRIGGER_TYPE.Inquiry) {
                    return actionTypes;
                }
                if (this.$store.getters[EDIT_AUTOMATION_TYPES.getters.allowCascadingRules]) {
                    actionTypes.push(ACTION_TYPE.ChangeStatus);
                }
                actionTypes.push(
                    ACTION_TYPE.ClearAssignee,
                    ACTION_TYPE.GenerateAttachment,
                    ACTION_TYPE.SetDataField,
                    ACTION_TYPE.SetDeadline,
                    ACTION_TYPE.ClearDataField
                );
                if (this.programData.ShowPauseResumeDeadlines) {
                    actionTypes.push(ACTION_TYPE.PauseDeadline, ACTION_TYPE.ResumeDeadline);
                }
                actionTypes.push(ACTION_TYPE.SatisfyDeadline, ACTION_TYPE.SignalToChannel);
                if (this.programData.ShowAccessGroups) {
                    actionTypes.push(ACTION_TYPE.AddAccessGroup, ACTION_TYPE.RemoveAccessGroup);
                }
                if (this.allowRequestESignatures) {
                    actionTypes.push(ACTION_TYPE.RequestESignature);
                }
                return actionTypes;
            },
            actionOptions() {
                return this.availableActionTypes.map((actionType) => ({
                    value: actionType,
                    text: getActionName(this.$app, actionType)
                }));
            },
            channelsWithRules() {
                return this.programData.Channels.filter((channel) => channel.Rules.length > 0).map((c) => ({
                    ...c,
                    hidden: c.IsDraft
                }));
            },
            selectedChannelSignals() {
                if (!this.internalRule.Action.ChannelId) {
                    return null;
                }
                const selectedChannel = this.channelsWithRules.find(
                    (channel) => channel.PublicId === this.internalRule.Action.ChannelId
                );
                return selectedChannel.Rules;
            },
            documentTemplates() {
                return this.programData.DocumentTemplates.map((dt) => ({ ...dt, hidden: !dt.TemplateUploaded }));
            },
            generateFileTypeOptions() {
                const document = this.programData.DocumentTemplates.find(
                    (dt) => dt.PublicId === this.internalRule.Action.DocumentTemplateId
                );
                if (!document) {
                    return [{ text: this.pdfDocumentOption, value: GENERATE_FILE_TYPE.default }];
                }
                switch (document.DocumentType) {
                    case DOCUMENT_TYPE.Ics:
                        return [{ text: this.icsDocumentOption, value: GENERATE_FILE_TYPE.default }];
                    case DOCUMENT_TYPE.Xlsx:
                        return [{ text: this.xlsxDocumentOption, value: GENERATE_FILE_TYPE.default }];
                    case DOCUMENT_TYPE.Docx:
                    case DOCUMENT_TYPE.Pdf:
                        return [
                            { text: this.pdfDocumentOption, value: GENERATE_FILE_TYPE.pdf },
                            { text: this.wordDocumentOption, value: GENERATE_FILE_TYPE.docx }
                        ];
                    case DOCUMENT_TYPE.Merge:
                        return [{ text: this.pdfDocumentOption, value: GENERATE_FILE_TYPE.default }];
                    default:
                        return [{ text: this.pdfDocumentOption, value: GENERATE_FILE_TYPE.default }];
                }
            },
            setDataSubTypeOptions() {
                return [
                    { value: SET_DATA_SUBACTION_TYPE.text, text: this[KEYS.setDataSubActionName_text] },
                    { value: SET_DATA_SUBACTION_TYPE.date, text: this[KEYS.setDataSubActionName_date] },
                    { value: SET_DATA_SUBACTION_TYPE.timestamp, text: this[KEYS.setDataSubActionName_timestamp] },
                    { value: SET_DATA_SUBACTION_TYPE.integer, text: this[KEYS.setDataSubActionName_integer] },
                    { value: SET_DATA_SUBACTION_TYPE.decimal, text: this[KEYS.setDataSubActionName_decimal] },
                    { value: SET_DATA_SUBACTION_TYPE.boolean, text: this[KEYS.setDataSubActionName_boolean] },
                    { value: SET_DATA_SUBACTION_TYPE.copy, text: this[KEYS.setDataSubActionName_copy] },
                    { value: SET_DATA_SUBACTION_TYPE.formula, text: this[KEYS.setDataSubActionName_formula] }
                ];
            },
            clearDataSubTypeOptions() {
                return [
                    { value: SET_DATA_SUBACTION_TYPE.text, text: this[KEYS.clearDataSubActionName_text] },
                    { value: SET_DATA_SUBACTION_TYPE.date, text: this[KEYS.clearDataSubActionName_date] },
                    { value: SET_DATA_SUBACTION_TYPE.timestamp, text: this[KEYS.clearDataSubActionName_timestamp] },
                    { value: SET_DATA_SUBACTION_TYPE.integer, text: this[KEYS.clearDataSubActionName_integer] },
                    { value: SET_DATA_SUBACTION_TYPE.decimal, text: this[KEYS.clearDataSubActionName_decimal] },
                    { value: SET_DATA_SUBACTION_TYPE.boolean, text: this[KEYS.clearDataSubActionName_boolean] }
                ];
            },
            setDataShowValue() {
                switch (this.setDataSubType) {
                    case SET_DATA_SUBACTION_TYPE.text:
                    case SET_DATA_SUBACTION_TYPE.integer:
                    case SET_DATA_SUBACTION_TYPE.decimal:
                        return true;
                    default:
                        return false;
                }
            },
            setDataShowBooleanOptions() {
                return this.setDataSubType === SET_DATA_SUBACTION_TYPE.boolean;
            },
            setDataSubTypeIsCopyOrFormula() {
                switch (this.setDataSubType) {
                    case SET_DATA_SUBACTION_TYPE.copy:
                    case SET_DATA_SUBACTION_TYPE.formula:
                        return true;
                    default:
                        return false;
                }
            },
            setDataAvailableSourceTypes() {
                function getId(idWithMarker, markerLength) {
                    return parseInt(idWithMarker.substring(markerLength));
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.copy) {
                    const options = this.fieldTypes.map((fieldType) => {
                        let text = null;
                        if (fieldType.PublicId.startsWith(FIELD_MARKER)) {
                            text = getDataFieldTypeDescription(
                                this.$app,
                                getId(fieldType.PublicId, FIELD_MARKER.length)
                            );
                        } else {
                            text = getDataFieldGroupTypeDescription(
                                this.$app,
                                getId(fieldType.PublicId, GROUP_MARKER.length)
                            );
                        }
                        return { value: fieldType.PublicId, text };
                    });
                    return options;
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.formula) {
                    const options = this.formulaTypes.map((formulaType) => {
                        const text = getDataFieldTypeDescription(this.$app, parseInt(formulaType.PublicId));
                        return { value: formulaType.PublicId, text };
                    });
                    return options;
                }
                return null;
            },
            setDataSourceTypeLabel() {
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.copy) {
                    return this[KEYS.setDataSourceFieldTypeLabel];
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.formula) {
                    return this[KEYS.setDataSourceFormulaTypeLabel];
                }
                return null;
            },
            setDataAvailableSources() {
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.copy) {
                    return this.programData.DataFields.filter((field) => field.TypeId === this.setDataSourceType);
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.formula) {
                    return this.programData.Formulas.filter(
                        (formula) => formula.ResultTypeId === parseInt(this.setDataSourceType)
                    );
                }
                return null;
            },
            setDataAvailableTargets() {
                let sourceTypeWithMarker = null;
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.copy) {
                    sourceTypeWithMarker = this.setDataSourceType;
                } else if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.formula) {
                    sourceTypeWithMarker = `f${this.setDataSourceType}`;
                } else if (this.setDataSubType) {
                    if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.text) {
                        // Choice fields can be written to as text fields
                        return this.programData.DataFields.filter(
                            (field) =>
                                !field.IsReadOnly &&
                                (field.TypeId === `f${DATA_FIELD_TYPE.String}` ||
                                    field.TypeId === `f${DATA_FIELD_TYPE.Choice}`)
                        );
                    }
                    const dataFieldType = SET_DATA_SUBACTION_TYPE_TO_DATA_FIELD_TYPE[this.setDataSubType];
                    sourceTypeWithMarker = `f${dataFieldType}`;
                }
                return this.programData.DataFields.filter(
                    (field) => !field.IsReadOnly && field.TypeId === sourceTypeWithMarker
                );
            },
            setDataTargetLabel() {
                if (this.setDataSubTypeIsCopyOrFormula) {
                    return this[KEYS.setDataCopyTargetLabel];
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.date) {
                    return this[KEYS.setDataCurrentDateTargetLabel];
                }
                if (this.setDataSubType === SET_DATA_SUBACTION_TYPE.timestamp) {
                    return this[KEYS.setDataCurrentTimestampTargetLabel];
                }
                return this[KEYS.setDataNormalTargetLabel];
            },
            setDataPvStorageMismatchMessage() {
                if (this.setDataSourceType === PV_SYSTEM_GROUP_ID) {
                    const sourceId = this.internalRule.Action.SourceDataId;
                    const targetId = this.internalRule.Action.DataId;
                    if (sourceId && targetId) {
                        const sourceConfig = this.getPvStorageConfig(sourceId);
                        const targetConfig = this.getPvStorageConfig(targetId);
                        if (sourceConfig === PV_STORAGE_CONFIG.always) {
                            if (targetConfig === PV_STORAGE_CONFIG.formula) {
                                return this.setDataPvStorageAlwaysToFormulaText;
                            } else if (targetConfig === PV_STORAGE_CONFIG.never) {
                                return this.setDataPvStorageAlwaysToNeverText;
                            }
                        } else if (sourceConfig === PV_STORAGE_CONFIG.formula) {
                            // If the formulas are *different* then show this warning
                            if (
                                sourceConfig.Attributes.ShowStorageEquipmentFormulaId !==
                                targetConfig.Attributes.ShowStorageEquipmentFormulaId
                            ) {
                                return this.setDataPvStorageFormulaToDifferentFormulaText;
                            } else if (targetConfig === PV_STORAGE_CONFIG.never) {
                                return this.setDataPvStorageFormulaToNeverText;
                            }
                        }
                    }
                }
                return "";
            },
            isSetDataPvStorageMismatch() {
                return this.setDataPvStorageMismatchMessage.length > 0;
            },
            setDataShowAppendConsecutive() {
                return this.setDataSubType === SET_DATA_SUBACTION_TYPE.text;
            },
            conditionLabel() {
                if (this.isValidationRule) {
                    return this[KEYS.conditionValidationLabel];
                }
                return this[KEYS.conditionActionLabel];
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        watch: {
            setDataSubType() {
                this.internalRule.Action.DataFieldValue = null;
                this.setDataSourceType = null;
                this.internalRule.Action.AppendConsecutiveNumber = null;
                this.internalRule.Action.SourceDataId = null;
                this.internalRule.Action.SourceFormulaId = null;
                this.internalRule.Action.DataId = null;
                this.validationHub.clearValidation();
            },
            setDataSourceType() {
                this.internalRule.Action.SourceDataId = null;
                this.internalRule.Action.SourceFormulaId = null;
                this.internalRule.Action.DataId = null;
                this.validationHub.clearValidation();
            },
            "internalRule.Action.Type"() {
                if (this.internalRule.Action.Type === ACTION_TYPE.ChangeStatus) {
                    this.internalRule.StopFurtherProcessing = true;
                } else {
                    this.internalRule.StopFurtherProcessing = false;
                    this.internalRule.Action.SatisfyAllDeadlines = false;
                    this.internalRule.Action.RemoveAllAccessGroups = false;
                }
                this.validationHub.clearValidation();
            },
            "internalRule.Action.SatisfyAllDeadlines"() {
                if (this.internalRule.Action.SatisfyAllDeadlines) {
                    this.internalRule.Action.DeadlineId = null;
                }
            },
            "internalRule.Action.RemoveAllAccessGroups"() {
                if (this.internalRule.Action.RemoveAllAccessGroups) {
                    this.internalRule.Action.AccessGroupId = null;
                }
            },
            "internalRule.Action.DocumentTemplateId"() {
                const document = this.programData.DocumentTemplates.find(
                    (dt) => dt.PublicId === this.internalRule.Action.DocumentTemplateId
                );
                if (!document) {
                    this.generateFileType = GENERATE_FILE_TYPE.default;
                    return;
                }
                if (document.DocumentType === DOCUMENT_TYPE.Pdf || document.DocumentType === DOCUMENT_TYPE.Docx) {
                    // Remember a docx type, otherwise default to PDF
                    if (this.generateFileType !== GENERATE_FILE_TYPE.docx) {
                        this.generateFileType = GENERATE_FILE_TYPE.pdf;
                    }
                } else {
                    this.generateFileType = GENERATE_FILE_TYPE.default;
                }
            }
        },
        mounted() {
            if (this.isESignatureTrigger) {
                this.internalRule.Frequency = RULE_FREQUENCY.EveryTime;
            }
        },
        created() {
            this.GENERATE_FILE_TYPE = GENERATE_FILE_TYPE;
            this.ACTION_TYPE = ACTION_TYPE;
            this.SET_DATA_SUBACTION_TYPE = SET_DATA_SUBACTION_TYPE;
            this.requiredValidator = getValidator(this.$app, VALIDATOR_TYPES.required);
        },
        methods: {
            getPvStorageConfig(pvGroupId) {
                // Remove the GROUP_MARKER character from the Id
                const pvPublicId = pvGroupId.substring(GROUP_MARKER.length);
                const pvGroup = this.programData.PVSystemGroups.find((group) => group.PublicId === pvPublicId);
                if (pvGroup) {
                    if (pvGroup.Attributes.ShowPvBatteryComponent) {
                        return pvGroup.Attributes.ShowStorageEquipmentFormulaId
                            ? PV_STORAGE_CONFIG.formula
                            : PV_STORAGE_CONFIG.always;
                    }
                    return PV_STORAGE_CONFIG.never;
                }
            },
            async [DIALOG_HOOK.isValidToClose](result) {
                if (result === DIALOG_RESULT.cancel) {
                    return true;
                }
                const validationErrors = this.validationHub.validate();

                return !validationErrors;
            },
            [DIALOG_HOOK.getResult](result) {
                if (result === DIALOG_RESULT.cancel) {
                    return null;
                }

                return this.internalRule;
            }
        }
    };
</script>
